import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL-backed state for a single string parameter.
 * Empty values remove the param from the URL.
 */
export function useUrlParam(
  key: string,
  defaultValue = ''
): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key) ?? defaultValue;

  const setValue = useCallback(
    (next: string) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          if (next && next !== defaultValue) params.set(key, next);
          else params.delete(key);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams, key, defaultValue]
  );

  return [value, setValue];
}

/**
 * URL-backed state constrained to a known set of values.
 * Falls back to defaultValue if URL contains an unknown value.
 * The default value is not serialized (kept out of the URL).
 */
export function useUrlParamEnum<T extends string>(
  key: string,
  values: readonly T[],
  defaultValue: T
): [T, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  const value: T = values.includes(raw as T) ? (raw as T) : defaultValue;

  const setValue = useCallback(
    (next: T) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          if (next === defaultValue) params.delete(key);
          else params.set(key, next);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams, key, defaultValue]
  );

  return [value, setValue];
}

/**
 * URL-backed state for a list of values serialized as comma-separated.
 * Empty arrays remove the param from the URL.
 */
export function useUrlParamCsv(
  key: string
): [string[], (value: string[]) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);

  // Memoize by raw string so identical URL state yields a stable array reference.
  // This avoids cascading re-renders when the array is used in deps or prop
  // comparisons downstream.
  const value = useMemo(
    () =>
      raw
        ? raw
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
        : [],
    [raw]
  );

  const setValue = useCallback(
    (next: string[]) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          if (next.length > 0) params.set(key, next.join(','));
          else params.delete(key);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams, key]
  );

  return [value, setValue];
}
